import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { localizedName } from '@/components/localized-name';
import { cn } from '@/lib/utils';
import {
  BENEFIT_TYPE,
  EVENT_TYPES,
  RECURRENCE,
  benefitAmountUnit,
} from './membership-enums';
import {
  useDeleteBenefit,
  useSaveBenefit,
  useServiceTypes,
  useUploadImage,
} from './memberships.api';
import type { MembershipBenefit } from './types';

/** Radix Select cannot hold an empty value; `NONE` stands in for "not set". */
const NONE = 'NONE';

const EMPTY_BENEFIT: Partial<MembershipBenefit> = {
  internalName: '',
  nameEn: '',
  nameEs: '',
  descriptionEn: '',
  descriptionEs: '',
  imageUrl: '',
  isActive: false,
  isPromoBenefit: false,
  ordering: 0,
  applicableEventType: 5,
  applicableServiceTypeId: null,
  recurrence: 0,
  type: 0,
  amount: 0,
  limit: 0,
};

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

/**
 * Create or edit one benefit on a membership plan.
 *
 * The amount's unit follows the benefit type — dollars for a fixed discount or
 * special price, percent for a percentage one, and nothing at all for the types
 * that carry no amount — which is what `getBenefitAmountSymbol` did in the
 * original.
 */
export function EditBenefitSheet({
  benefit,
  planId,
  open,
  onOpenChange,
}: {
  /** `null` opens the sheet on a blank benefit. */
  benefit: MembershipBenefit | null;
  planId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Partial<MembershipBenefit>>(EMPTY_BENEFIT);

  const { data: serviceTypes, isPending: loadingServiceTypes } =
    useServiceTypes(open);
  const save = useSaveBenefit();
  const remove = useDeleteBenefit();
  const upload = useUploadImage('benefits');

  useEffect(() => {
    if (!open) return;
    setDraft(benefit ? { ...benefit } : { ...EMPTY_BENEFIT, subscriptionId: planId });
  }, [benefit, planId, open]);

  const set = (patch: Partial<MembershipBenefit>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const isEditing = Boolean(benefit?.id);
  const unit = benefitAmountUnit(draft.type);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b">
          <SheetTitle>
            {isEditing
              ? t('memberships.editBenefit')
              : t('memberships.addBenefit')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={remove.isPending}
                >
                  {remove.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 />
                  )}
                  {t('memberships.deleteBenefit')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('memberships.deleteBenefitConfirmTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('memberships.deleteBenefitConfirmBody')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(benefit!.id, {
                        onSuccess: () => {
                          toast.success(t('memberships.benefitDeleted'));
                          onOpenChange(false);
                        },
                        onError: () =>
                          toast.error(t('memberships.deleteFailed')),
                      })
                    }
                  >
                    {t('memberships.deleteBenefit')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('memberships.isActive')}
            </Label>
            <Switch
              checked={Boolean(draft.isActive)}
              onCheckedChange={(isActive) => set({ isActive })}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('memberships.isPromoBenefit')}
            </Label>
            <Switch
              checked={Boolean(draft.isPromoBenefit)}
              onCheckedChange={(isPromoBenefit) => set({ isPromoBenefit })}
            />
          </div>

          <Field label={t('memberships.ordering')}>
            <Input
              type="number"
              value={draft.ordering ?? 0}
              onChange={(e) => set({ ordering: Number(e.target.value) })}
            />
          </Field>

          <Field label={t('memberships.internalName')}>
            <Input
              value={draft.internalName ?? ''}
              onChange={(e) => set({ internalName: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('memberships.name')} (EN)`}>
              <Input
                value={draft.nameEn ?? ''}
                onChange={(e) => set({ nameEn: e.target.value })}
              />
            </Field>
            <Field label={`${t('memberships.name')} (ES)`}>
              <Input
                value={draft.nameEs ?? ''}
                onChange={(e) => set({ nameEs: e.target.value })}
              />
            </Field>
          </div>

          <Field label={`${t('memberships.description')} (EN)`}>
            <Textarea
              rows={3}
              value={draft.descriptionEn ?? ''}
              onChange={(e) => set({ descriptionEn: e.target.value })}
            />
          </Field>
          <Field label={`${t('memberships.description')} (ES)`}>
            <Textarea
              rows={3}
              value={draft.descriptionEs ?? ''}
              onChange={(e) => set({ descriptionEs: e.target.value })}
            />
          </Field>

          <Field label={t('memberships.applicableEventType')}>
            <Select
              value={String(draft.applicableEventType ?? 5)}
              onValueChange={(v) => set({ applicableEventType: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('memberships.applicableServiceType')}>
            {loadingServiceTypes ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                value={draft.applicableServiceTypeId ?? NONE}
                onValueChange={(v) =>
                  set({ applicableServiceTypeId: v === NONE ? null : v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('memberships.any')}</SelectItem>
                  {(serviceTypes ?? []).map((serviceType) => (
                    <SelectItem key={serviceType.id} value={serviceType.id}>
                      {localizedName(serviceType, i18n.resolvedLanguage ?? 'en')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label={t('memberships.recurrence')}>
            <Select
              value={String(draft.recurrence ?? 0)}
              onValueChange={(v) => set({ recurrence: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('memberships.type')}>
            <Select
              value={String(draft.type ?? 0)}
              onValueChange={(v) => set({ type: Number(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BENEFIT_TYPE.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Free and early-visibility carry no amount, so the field goes. */}
          {unit !== '' && (
            <Field label={`${t('memberships.amount')} ${unit}`}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.amount ?? 0}
                onChange={(e) => set({ amount: Number(e.target.value) })}
              />
            </Field>
          )}

          <Field label={t('memberships.limit')}>
            <Input
              type="number"
              min={0}
              value={draft.limit ?? 0}
              onChange={(e) => set({ limit: Number(e.target.value) })}
            />
          </Field>

          <Field label={t('memberships.image')}>
            <div className="space-y-2">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="bg-muted max-h-40 w-full rounded-lg object-contain"
                />
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  upload.mutate(file, {
                    onSuccess: (url) => set({ imageUrl: url }),
                    onError: () => toast.error(t('memberships.uploadFailed')),
                  });
                  event.target.value = '';
                }}
              />
              <Button
                variant="outline"
                className="w-full border-dashed"
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {upload.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ImagePlus />
                )}
                {t('memberships.clickOrDrag')}
              </Button>
            </div>
          </Field>

          <Button
            className="w-full"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                { ...draft, subscriptionId: draft.subscriptionId ?? planId },
                {
                  onSuccess: () => {
                    toast.success(t('memberships.benefitSaved'));
                    onOpenChange(false);
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('memberships.saveFailed')
                    ),
                }
              )
            }
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
            {isEditing ? t('memberships.update') : t('memberships.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
